import CheckboxComponent from '../../../../components/stories/atoms/checkboxes'
import { checkboxSizeOptions } from '../../../../components/stories/atoms/checkboxes/constants'
import {
  Folder,
  Plan
} from '../../../../components/stories/atoms/icons/entities'
import {
  ChevronDown,
  ChevronRight,
  Confirm
} from '../../../../components/stories/atoms/icons/essentials'
import { iconSizeOptions } from '../../../../components/stories/atoms/icons/icons-sizes'
import colorsOptions from '../../../../components/stories/fundamentals/colors-styles/constants'
import {
  textStyleSizeOptions,
  textStyleWeightOptions
} from '../../../../components/stories/fundamentals/text-styles/constants'
import { TableRowText } from '../../../../components/stories/organisms/table/components/row/components/table-row-text'
import { TableRowPathCell } from '../../../../components/stories/organisms/table/components/row/components/table-row-path-cell'
import {
  RowText,
  RowTextContainer
} from '../../../../components/stories/organisms/table/components/row/styled-components'
import TableRowCell from '../../../../components/stories/organisms/table/components/row/table-row-cell'
import { RowLeadingContainer } from '../../../../components/stories/organisms/table/styled-components'
import {
  tableColumnSizeOptions,
  tableHeaderOrientationOptions,
  tableHeaderTypeOptions
} from '../../../../components/stories/organisms/table/constants'
import {
  planTreeItemTypeOptions,
  planTreeSortByOptions
} from '../../../../react-query/plan/query/use-get-plan-tree-query'
import { DragNDrop } from '../../../../components/stories/atoms/icons/table'
import SkeletonComponent from '../../../../components/stories/atoms/skeleton'
import BadgesComponent from '../../../../components/stories/atoms/badges'
import {
  badgeColorOptions,
  badgeSizeOptions,
  badgesVariantOptions
} from '../../../../components/stories/atoms/badges/constants'
import PlanMenuContextMenu from '../plan-menu-context-menu'
import PlanMenuSelectRevisionMenu from './components/plan-menu-select-revision-menu'
import { formatRelativeDate } from '../../../../utils/format-relative-date'
import { useGetRelativePlanPathQuery } from '../../../../react-query/plan/query/use-get-relative-plan-path-query'
import { useIntl } from 'react-intl'

const PlanPathCell = ({ planId }) => {
  const { formatMessage } = useIntl()
  const {
    data: path,
    isLoading,
    isError
  } = useGetRelativePlanPathQuery({
    planId
  })

  const hasPath = path?.length > 0

  const displayPath = hasPath
    ? `/${path?.join('/')}`
    : formatMessage({ id: 'main_folder', defaultMessage: 'Main folder' })

  return (
    <TableRowPathCell
      path={displayPath}
      isLoading={isLoading}
      isError={isError}
    />
  )
}

const planMenuTableSkeletonRowCount = 8
const TREE_INDENT_PX = 40

export const planMenuDragHandleColumnId = 'drag'

const planMenuFormatMessageFallback = ({ id, defaultMessage }, values) => {
  if (id === 'projects_items') {
    return `${values?.entries ?? 0} ${values?.entries === 1 ? 'item' : 'items'}`
  }

  return defaultMessage ?? id
}

const getItemType = (item) =>
  item.type === planTreeItemTypeOptions.FOLDER
    ? planTreeItemTypeOptions.FOLDER
    : planTreeItemTypeOptions.PLAN

const formatLastActivity = (lastActivity, locale, formatMessage) => {
  if (!lastActivity) return ''

  return formatRelativeDate(lastActivity, locale, formatMessage)
}

const renderTableCellSkeleton = () => (
  <SkeletonComponent
    isLoading
    skeletonShape={{
      width: '100%',
      height: '16px',
      borderRadius: '6px'
    }}
  />
)

const renderTitleCellSkeleton = () => (
  <TableRowCell orientation={tableHeaderOrientationOptions.LEFT}>
    {renderTableCellSkeleton()}
  </TableRowCell>
)

const OpenTasksCell = ({ row }) => {
  if (row.original.type === planTreeItemTypeOptions.FOLDER) return null

  return (
    <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
      <TableRowText
        mainText={
          <RowText
            weight={textStyleWeightOptions['TEXT-500']}
            size={textStyleSizeOptions['TEXT-S']}
            color={
              row.original.active
                ? colorsOptions.greys['grey-90']
                : colorsOptions.greys['grey-40']
            }
          >
            {row.original.openTasks}
          </RowText>
        }
      />
    </TableRowCell>
  )
}

export const buildDataSkeleton = (rowCount = planMenuTableSkeletonRowCount) =>
  Array.from({ length: rowCount }, (_, index) => ({
    id: `plan-menu-table-skeleton-${index}`,
    name: '',
    type: planTreeItemTypeOptions.PLAN,
    active: true,
    parentId: null,
    ancestorFolderIds: [],
    items: 0,
    permissions: [],
    version: 0,
    openPins: 0,
    openTasks: 0,
    lastActivity: null,
    lastActivityLabel: '',
    isDraggable: false,
    isDroppable: false,
    isSkeleton: true
  }))

export const buildData = (
  data,
  {
    childrenByFolderId = new Map(),
    parentId = null,
    ancestorFolderIds = [],
    locale,
    formatMessage = planMenuFormatMessageFallback
  } = {}
) => {
  if (!data) return []

  return data.map((item) => {
    const type = getItemType(item)
    const isFolder = type === planTreeItemTypeOptions.FOLDER
    const itemParentId = item.parentId ?? parentId
    const folderChildren = childrenByFolderId.get(item.id)

    return {
      id: item.id,
      name: item.name,
      type,
      active: item.active ?? true,
      parentId: itemParentId,
      ancestorFolderIds,
      items: item.items ?? 0,
      permissions: item.permissions ?? [],
      version: item.version ?? 1,
      openPins: item.openPins ?? 0,
      openTasks: item.openTasks ?? 0,
      lastActivity: item.lastActivity,
      lastActivityLabel: formatLastActivity(
        item.lastActivity,
        locale,
        formatMessage
      ),
      isDraggable: true,
      isDroppable: isFolder,
      subRows:
        isFolder && folderChildren
          ? buildData(folderChildren, {
              childrenByFolderId,
              parentId: item.id,
              ancestorFolderIds: [...ancestorFolderIds, item.id],
              locale,
              formatMessage
            })
          : undefined
    }
  })
}

export const buildColumnsDefault = ({
  formatMessage = planMenuFormatMessageFallback,
  isSearching = false,
  hasFolderRow = false,
  // plan handlers
  onOpenPlan = () => {}
} = {}) => [
  {
    id: planMenuDragHandleColumnId,
    accessorFn: (row) => row.isDraggable,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: (info) => (
      <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
        {info.getValue() && <DragNDrop size={iconSizeOptions['ICON-S']} />}
      </TableRowCell>
    ),
    meta: {
      type: tableHeaderTypeOptions.HANDLE,
      orientation: tableHeaderOrientationOptions.CENTER,
      showIcon: true
    }
  },
  {
    id: planTreeSortByOptions.TITLE,
    accessorKey: 'name',
    header: formatMessage({ id: 'title', defaultMessage: 'Title' }),
    cell: ({ row }) => {
      const isFolder = row.original.type === planTreeItemTypeOptions.FOLDER
      const isPlan = row.original.type === planTreeItemTypeOptions.PLAN
      const isActive = row.original.active
      const handleToggleFolder = row.getToggleExpandedHandler()
      const handleOpenPlan = () => onOpenPlan(row.original.id)

      return (
        <TableRowCell
          orientation={tableHeaderOrientationOptions.LEFT}
          style={{
            paddingLeft: `${row.depth * TREE_INDENT_PX}px`
          }}
        >
          {isFolder && (
            <RowLeadingContainer
              onClick={handleToggleFolder}
              style={{ cursor: 'pointer' }}
            >
              {row.getIsExpanded() ? (
                <ChevronDown size={iconSizeOptions['ICON-S']} />
              ) : (
                <ChevronRight size={iconSizeOptions['ICON-S']} />
              )}
            </RowLeadingContainer>
          )}
          <TableRowText
            leadingComponent={
              isFolder ? (
                <Folder size={iconSizeOptions['ICON-L']} />
              ) : (
                <Plan
                  size={iconSizeOptions['ICON-L']}
                  stroke={
                    isActive
                      ? colorsOptions.greys['grey-90']
                      : colorsOptions.greys['grey-40']
                  }
                  style={hasFolderRow ? { marginLeft: '3rem' } : undefined}
                />
              )
            }
            mainText={
              <RowText
                as={isPlan ? 'button' : 'span'}
                type={isPlan ? 'button' : undefined}
                onClick={isPlan ? handleOpenPlan : undefined}
                weight={textStyleWeightOptions['TEXT-600']}
                size={textStyleSizeOptions['TEXT-R']}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: isPlan ? 'pointer' : 'default',
                  padding: 0,
                  textAlign: 'left',
                  color: isActive
                    ? colorsOptions.greys['grey-90']
                    : colorsOptions.greys['grey-40']
                }}
              >
                {row.original.name}
              </RowText>
            }
            secondaryText={
              <RowTextContainer>
                {isFolder && (
                  <RowText
                    weight={textStyleWeightOptions['TEXT-500']}
                    size={textStyleSizeOptions['TEXT-XS']}
                  >
                    {formatMessage(
                      {
                        id: 'projects_items',
                        defaultMessage: '{entries} items'
                      },
                      { entries: row.original.items }
                    )}
                  </RowText>
                )}
                {isPlan && isSearching && (
                  <PlanPathCell planId={row.original.id} />
                )}
              </RowTextContainer>
            }
          />
          {!isActive && (
            <BadgesComponent
              variant={badgesVariantOptions.HIGHLIGHTED}
              color={badgeColorOptions.DISABLED}
              size={badgeSizeOptions.SMALL}
            >
              {formatMessage({ id: 'inactive' })}
            </BadgesComponent>
          )}
        </TableRowCell>
      )
    },
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT,
      isFlexible: true,
      minWidth: 240
    }
  },
  {
    accessorKey: 'revisions',
    header: formatMessage({
      id: 'plans_table_header_revision_abbreviation',
      defaultMessage: 'Revisions'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: ({ row }) => {
      if (row.original.type === planTreeItemTypeOptions.FOLDER) return null

      return (
        <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
          <PlanMenuSelectRevisionMenu item={row.original} />
        </TableRowCell>
      )
    },
    meta: {
      // the whole row is the drag surface, so cells that own a control of their
      // own opt out of it (see plan-menu-table-row)
      preventDrag: true,
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    id: planTreeSortByOptions.LAST_ACTIVITY,
    accessorKey: 'lastActivity',
    header: formatMessage({
      id: 'projects_lastActivity',
      defaultMessage: 'Last Activity'
    }),
    size: tableColumnSizeOptions.M,
    cell: ({ row }) => {
      if (row.original.type === planTreeItemTypeOptions.FOLDER) return null

      return (
        <TableRowCell>
          <TableRowText
            mainText={
              <RowText
                weight={textStyleWeightOptions['TEXT-500']}
                size={textStyleSizeOptions['TEXT-S']}
                color={
                  row.original.active
                    ? colorsOptions.greys['grey-90']
                    : colorsOptions.greys['grey-40']
                }
              >
                {row.original.lastActivityLabel}
              </RowText>
            }
          />
        </TableRowCell>
      )
    },
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'openPins',
    header: formatMessage({
      id: 'plans_openPins',
      defaultMessage: 'Open Pins'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: ({ row }) => {
      if (row.original.type === planTreeItemTypeOptions.FOLDER) return null

      return (
        <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
          <TableRowText
            mainText={
              <RowText
                weight={textStyleWeightOptions['TEXT-500']}
                size={textStyleSizeOptions['TEXT-S']}
                color={
                  row.original.active
                    ? colorsOptions.greys['grey-90']
                    : colorsOptions.greys['grey-40']
                }
              >
                {row.original.openPins}
              </RowText>
            }
          />
        </TableRowCell>
      )
    },
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    accessorKey: 'openTasks',
    header: formatMessage({
      id: 'plans_openTasks',
      defaultMessage: 'Open Tasks'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: OpenTasksCell,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    accessorKey: 'contextMenu',
    header: null,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: ({ row }) => (
      <TableRowCell>
        <PlanMenuContextMenu item={row.original} />
      </TableRowCell>
    ),
    meta: {
      preventDrag: true,
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    id: 'select',
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: ({ row }) => (
      <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
        <CheckboxComponent.Root
          size={checkboxSizeOptions.MEDIUM}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={row.getToggleSelectedHandler()}
        >
          <CheckboxComponent.Indicator>
            <Confirm size={iconSizeOptions['ICON-S']} />
          </CheckboxComponent.Indicator>
        </CheckboxComponent.Root>
      </TableRowCell>
    ),
    meta: {
      preventDrag: true,
      header: {
        type: tableHeaderTypeOptions.CHECKBOX,
        orientation: tableHeaderOrientationOptions.CENTER,
        checkbox: {
          size: checkboxSizeOptions.LARGE
        }
      }
    }
  }
]

export const buildColumnsSkeleton = ({
  formatMessage = planMenuFormatMessageFallback
} = {}) => [
  {
    id: planMenuDragHandleColumnId,
    accessorFn: (row) => row.isDraggable,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: () => (
      <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
        {renderTableCellSkeleton()}
      </TableRowCell>
    ),
    meta: {
      type: tableHeaderTypeOptions.HANDLE,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    id: planTreeSortByOptions.TITLE,
    accessorKey: 'name',
    header: formatMessage({ id: 'title', defaultMessage: 'Title' }),
    enableSorting: false,
    cell: renderTitleCellSkeleton,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT,
      isFlexible: true,
      minWidth: 229
    }
  },
  {
    accessorKey: 'revisions',
    header: formatMessage({
      id: 'plans_table_header_revision_abbreviation',
      defaultMessage: 'Revisions'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: planTreeSortByOptions.LAST_ACTIVITY,
    accessorKey: 'lastActivity',
    header: formatMessage({
      id: 'projects_lastActivity',
      defaultMessage: 'Last Activity'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'openPins',
    header: formatMessage({
      id: 'plans_openPins',
      defaultMessage: 'Open pins'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.EMPTY,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'openTasks',
    header: formatMessage({
      id: 'plans_openTasks',
      defaultMessage: 'Open tasks'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.EMPTY,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: 'select',
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: () => (
      <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
        {renderTableCellSkeleton()}
      </TableRowCell>
    ),
    meta: {
      header: {
        type: tableHeaderTypeOptions.EMPTY,
        orientation: tableHeaderOrientationOptions.CENTER
      }
    }
  }
]
